import { sx } from '../../lib/sx'
import { Hov } from '../../lib/Hov'

/**
 * The other half of a zero-knowledge pairing: scan the code the inviting device
 * is holding up. Tapping the viewfinder stands in for a successful scan.
 *
 * Identical on both handsets — either device can be the one scanning.
 */
export function QrScanner({ onBack, onScan }: { onBack: () => void; onScan: () => void }) {
  return (
    <div style={sx('position:absolute;inset:0;padding-top:64px;display:flex;flex-direction:column;background:#08080a')}>
      <div style={sx('padding:0 20px 16px;display:flex;flex-direction:column;gap:12px;flex:none')}>
        <button
          onClick={onBack}
          style={sx(
            'width:36px;height:36px;flex:none;border-radius:var(--radius-full);border:none;' +
              'background:rgba(255,255,255,.12);cursor:pointer;display:flex;align-items:center;justify-content:center'
          )}
        >
          <span
            style={sx(
              'width:18px;height:18px;background:#fff;' +
                '-webkit-mask:url(/assets/icons/arrow-left.svg) center/contain no-repeat;' +
                'mask:url(/assets/icons/arrow-left.svg) center/contain no-repeat'
            )}
          />
        </button>
        <span style={sx('font-size:var(--fs-xl);font-weight:var(--fw-bold);color:#fff')}>
          Scan to pair
        </span>
        <span style={sx('font-size:var(--fs-xs);line-height:18px;color:rgba(255,255,255,.6)')}>
          Point this at the code on the other device. Neither of you learns whose device the other
          one is.
        </span>
      </div>

      <div style={sx('flex:1;min-height:0;display:flex;align-items:center;justify-content:center;padding:0 20px')}>
        <Hov
          as="button"
          onClick={onScan}
          title="Simulate a successful scan"
          style={sx(
            'width:240px;height:240px;position:relative;border-radius:var(--radius-lg);' +
              'border:none;padding:0;cursor:pointer;' +
              'background:radial-gradient(circle at 50% 45%,rgba(62,95,255,.16) 0%,transparent 62%),#111116'
          )}
          hoverStyle={sx('background:radial-gradient(circle at 50% 45%,rgba(62,95,255,.28) 0%,transparent 62%),#14141b')}
        >
          <Corner top left />
          <Corner top />
          <Corner left />
          <Corner />
          <span
            style={sx(
              'position:absolute;left:0;right:0;top:50%;height:2px;' +
                'background:linear-gradient(90deg,transparent,var(--bg-accent),transparent)'
            )}
          />
          <span
            style={sx(
              'position:absolute;left:0;right:0;bottom:20px;text-align:center;' +
                'font-size:var(--fs-2xs);color:rgba(255,255,255,.5)'
            )}
          >
            Tap to simulate a scan
          </span>
        </Hov>
      </div>

      <div style={sx('padding:0 20px 28px;flex:none')}>
        <span
          style={sx(
            'display:flex;gap:10px;padding:12px 14px;border-radius:var(--radius-md);' +
              'background:rgba(255,255,255,.06)'
          )}
        >
          <span
            style={sx(
              'width:18px;height:18px;flex:none;background:rgba(255,255,255,.5);' +
                '-webkit-mask:url(/assets/icons/lock.svg) center/contain no-repeat;' +
                'mask:url(/assets/icons/lock.svg) center/contain no-repeat'
            )}
          />
          <span style={sx('font-size:var(--fs-xs);line-height:18px;color:rgba(255,255,255,.6)')}>
            Paired devices send each other the threats they block. Nothing links either handset to
            the person holding it, and either side can stop at any time.
          </span>
        </span>
      </div>
    </div>
  )
}

/** Viewfinder bracket. */
function Corner({ top, left }: { top?: boolean; left?: boolean }) {
  return (
    <span
      style={sx(
        `position:absolute;${top ? 'top' : 'bottom'}:18px;${left ? 'left' : 'right'}:18px;` +
          `width:34px;height:34px;` +
          `border-${top ? 'top' : 'bottom'}:2px solid rgba(255,255,255,.85);` +
          `border-${left ? 'left' : 'right'}:2px solid rgba(255,255,255,.85);` +
          `border-radius:${top && left ? '10px 0 0 0' : top ? '0 10px 0 0' : left ? '0 0 0 10px' : '0 0 10px 0'}`
      )}
    />
  )
}
